import { normalizePixKey, resolvePixKeyType } from "@/shared/utils/pixKey";

import type { CreateClientAccountRequest } from "../types/clients-api.types";
import { PublicAccountPaymentMethod } from "../types/clients-api.types";

// Maps the BRL|Pix select codes (0..4) back to the labels the backend expects
// in account_metadata. The generated `account_key` column coalesces `llavePix`
// (not `pix_key`), and the dispersion auto-fill reads `tipoLlavePix`, so accounts
// must be stored with these legacy keys/labels.
const PIX_TYPE_CODE_TO_LABEL: Record<string, string> = {
  "0": "CPF",
  "1": "CNPJ",
  "2": "Email",
  "3": "Teléfono",
  "4": "EVP",
};

function mapPixMetadata(
  currency: string,
  bankMethod: string,
  fields: Record<string, string>,
): Record<string, string> {
  if (currency.trim() !== "BRL" || bankMethod.trim() !== "Pix") {
    return { ...fields };
  }
  const { pix_type, pix_key, ...rest } = fields;
  const next: Record<string, string> = { ...rest };
  const label = PIX_TYPE_CODE_TO_LABEL[(pix_type ?? "").trim()];
  const keyValue = (pix_key ?? "").trim();
  if (label) next.tipoLlavePix = label;
  if (keyValue) {
    // The form stores the masked display value (CPF "000.000.000-00", phone "(11) 98765-4321") — persist
    // the raw key instead (digits / lowercase), the shape `account_key` and the dispersion autofill expect.
    const type = resolvePixKeyType(pix_type);
    next.llavePix = type ? normalizePixKey(type, keyValue) : keyValue;
  }
  return next;
}

function pruneBinanceUidEmailMetadata(
  paymentMethod: PublicAccountPaymentMethod,
  currency: string,
  bankMethod: string,
  fields: Record<string, string>,
): Record<string, string> {
  const c = currency.trim();
  const b = bankMethod.trim();
  if (paymentMethod !== PublicAccountPaymentMethod.BINANCE_PAY || c !== "USDT" || b !== "BINANCE") {
    return { ...fields };
  }
  const uid = (fields.binancePayUid ?? "").trim();
  const email = (fields.binancePayEmail ?? "").trim();
  const next: Record<string, string> = { ...fields };
  if (uid) {
    delete next.binancePayEmail;
    next.binancePayUid = uid;
  } else if (email) {
    delete next.binancePayUid;
    next.binancePayEmail = email;
  }
  return next;
}

export function buildCreateClientAccountPayload(
  currency: string,
  bankMethod: string,
  paymentMethod: PublicAccountPaymentMethod,
  methodFields: Record<string, string>,
  alias?: string,
): CreateClientAccountRequest {
  const c = currency.trim();
  const b = bankMethod.trim();
  const aliasTrimmed = alias?.trim();
  const payload: CreateClientAccountRequest = {
    currency: c,
    bank_name: b,
    payment_method: paymentMethod,
    account_metadata: mapPixMetadata(c, b, pruneBinanceUidEmailMetadata(paymentMethod, c, b, methodFields)),
  };
  if (aliasTrimmed) payload.alias = aliasTrimmed;
  return payload;
}
